import { config } from "../config";
import type {
  CommandAckRequest,
  DeviceCredential,
  HeartbeatRequest,
  HeartbeatResponse,
  PollPairingRequest,
  PollPairingResponse,
  ProofOfPlayRequest,
  ProofOfPlayResult,
  RegisterRequest,
  RegisterResponse,
  ScreenshotUploadResult,
  SyncResponse,
} from "./types";

// Typed client for the device API. Owns the access/refresh credential and
// transparently refreshes on a 401. All authed calls go through `authedFetch`,
// and every caller shares ONE refresh in flight: sync, heartbeat and the
// proof-of-play flush hit 401 on the same tick when the access token expires,
// and each refreshing with the same refresh token would strand the device.

export class DeviceApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = "DeviceApiError";
  }
}

/**
 * Thrown when the backend signals the device is permanently deprovisioned
 * (unpaired / retired / token generation revoked, or a dead refresh token).
 * The runtime catches this and wipes credentials + re-enters pairing.
 * Distinct from transient network/5xx errors, which keep the device playing.
 */
export class DeviceDeprovisionedError extends Error {
  constructor(message = "device deprovisioned") {
    super(message);
    this.name = "DeviceDeprovisionedError";
  }
}

/**
 * Thrown when the device still holds a pairing server-side but its refresh token was
 * superseded by one it never received (a lost refresh response, retried too late).
 * NOT a revocation: the device must not wipe itself. The runtime keeps playing from
 * cache and waits for the operator to unpair and re-pair it.
 */
export class DeviceCredentialLostError extends Error {
  constructor(message = "device credential superseded") {
    super(message);
    this.name = "DeviceCredentialLostError";
  }
}

/** Read the backend domain error code from a response body (best-effort). */
async function readErrorCode(res: Response): Promise<string | null> {
  try {
    const body = await res.clone().json();
    return body?.error?.code ?? null;
  } catch {
    return null;
  }
}

let credential: DeviceCredential | null = null;
// Bumped whenever the credential is replaced from outside a refresh (pairing, wipe),
// so a refresh still in flight cannot write a credential back over a deprovision.
let credentialEpoch = 0;
let refreshInFlight: Promise<boolean> | null = null;
let onCredentialChange: ((cred: DeviceCredential) => void | Promise<void>) | null = null;

export function setCredential(cred: DeviceCredential | null): void {
  credential = cred;
  credentialEpoch += 1;
}

export function setCredentialChangeHandler(
  fn: (cred: DeviceCredential) => void | Promise<void>,
): void {
  onCredentialChange = fn;
}

const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

function url(path: string): string {
  return `${config.apiBaseUrl}${path}`;
}

async function parseJson<T>(res: Response): Promise<T> {
  const text = await res.text();
  return text ? (JSON.parse(text) as T) : ({} as T);
}

async function postJson<TReq, TRes>(path: string, body: TReq): Promise<TRes> {
  const res = await fetch(url(path), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new DeviceApiError(res.status, `POST ${path} -> ${res.status}`);
  return parseJson<TRes>(res);
}

/**
 * Rotate the access token. Every concurrent caller shares the same refresh.
 * Resolves true once a newer credential is in hand; false on a transient failure
 * (the caller keeps playing from cache). Throws DeviceDeprovisionedError when the
 * server revoked the device, DeviceCredentialLostError when the token was superseded.
 */
function refreshCredential(): Promise<boolean> {
  if (!refreshInFlight) {
    refreshInFlight = doRefresh().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

async function doRefresh(): Promise<boolean> {
  const sent = credential;
  if (!sent) return false;
  const epoch = credentialEpoch;
  const delays = [0, ...config.refreshRetryDelaysMs];

  for (const delay of delays) {
    if (delay > 0) await wait(delay);
    if (credentialEpoch !== epoch) return false; // wiped or re-paired meanwhile
    let res: Response;
    const abort = new AbortController();
    const timeout = setTimeout(() => abort.abort(), config.refreshTimeoutMs);
    try {
      res = await fetch(url("/refresh"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh_token: sent.refresh_token }),
        signal: abort.signal,
      });
    } catch {
      continue; // network error/timeout: the server may have rotated anyway — retry inside the grace window
    } finally {
      clearTimeout(timeout);
    }
    if (res.status === 502 || res.status === 503 || res.status === 504) {
      continue; // a gateway can fail AFTER the backend committed the rotation — same as a lost response
    }
    if (res.status === 401) {
      const code = await readErrorCode(res);
      if (code === "DEVICE_REFRESH_SUPERSEDED") throw new DeviceCredentialLostError(code);
      throw new DeviceDeprovisionedError(code ?? "refresh_rejected");
    }
    if (!res.ok) return false; // 403 (proxy/WAF), 5xx: transient
    const next = await parseJson<DeviceCredential>(res);
    if (credentialEpoch !== epoch) return false; // never resurrect a wiped credential
    credential = next;
    await onCredentialChange?.(next); // persisted before anyone uses it
    return true;
  }
  return false;
}

async function authedFetch(path: string, init: RequestInit, retry = true): Promise<Response> {
  if (!credential) throw new DeviceApiError(401, "no credential");
  const sentToken = credential.token;
  const headers = new Headers(init.headers);
  headers.set("Authorization", `Bearer ${sentToken}`);
  const res = await fetch(url(path), { ...init, headers });
  if (res.status === 401) {
    // Explicit revoke signal → deprovision immediately (no point refreshing).
    if ((await readErrorCode(res)) === "DEVICE_REVOKED") {
      throw new DeviceDeprovisionedError("DEVICE_REVOKED");
    }
    if (retry) {
      // Another caller already refreshed while this request was out: use that.
      if (credential && credential.token !== sentToken) {
        return authedFetch(path, init, false);
      }
      // Otherwise the access token expired — refresh (shared) and retry once.
      if (await refreshCredential()) return authedFetch(path, init, false);
    }
  }
  return res;
}

/** test-only: clear module state */
export function __resetClient(): void {
  credential = null;
  credentialEpoch = 0;
  refreshInFlight = null;
  onCredentialChange = null;
}

// ── Public API ───────────────────────────────────────────────────────────

export function register(body: RegisterRequest): Promise<RegisterResponse> {
  return postJson<RegisterRequest, RegisterResponse>("/register", body);
}

export function pollPairing(body: PollPairingRequest): Promise<PollPairingResponse> {
  return postJson<PollPairingRequest, PollPairingResponse>("/poll-pairing", body);
}

/** Returns the schedule, or null on a 304 Not Modified. */
export async function sync(etag: string | null): Promise<SyncResponse | null> {
  const headers = new Headers();
  if (etag) headers.set("If-None-Match", etag);
  const res = await authedFetch("/sync", { method: "GET", headers });
  if (res.status === 304) return null;
  if (!res.ok) throw new DeviceApiError(res.status, `GET /sync -> ${res.status}`);
  return parseJson<SyncResponse>(res);
}

export async function heartbeat(body: HeartbeatRequest): Promise<HeartbeatResponse | null> {
  const res = await authedFetch("/heartbeat", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new DeviceApiError(res.status, `POST /heartbeat -> ${res.status}`);
  // Every beat carries the authoritative clock — the device's only periodic
  // time source (a 304 sync skips server_time). Tolerate an empty body from a
  // pre-upgrade backend: no refresh is better than a crash-loop.
  if (res.status === 204) return null;
  try {
    return await parseJson<HeartbeatResponse>(res);
  } catch {
    return null;
  }
}

export async function sendProofOfPlay(body: ProofOfPlayRequest): Promise<ProofOfPlayResult> {
  const res = await authedFetch("/proof-of-play", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new DeviceApiError(res.status, `POST /proof-of-play -> ${res.status}`);
  return parseJson<ProofOfPlayResult>(res);
}

export async function ackCommand(body: CommandAckRequest): Promise<void> {
  const res = await authedFetch("/command-ack", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new DeviceApiError(res.status, `POST /command-ack -> ${res.status}`);
}

export async function uploadScreenshot(
  commandId: string | null,
  blob: Blob,
): Promise<ScreenshotUploadResult> {
  const form = new FormData();
  // Filename is cosmetic; the backend derives the extension from content type.
  form.append("file", blob, "screenshot.png");
  if (commandId) form.append("command_id", commandId);
  // No explicit Content-Type — the browser sets the multipart boundary.
  const res = await authedFetch("/screenshot", { method: "POST", body: form });
  if (!res.ok) throw new DeviceApiError(res.status, `POST /screenshot -> ${res.status}`);
  return parseJson<ScreenshotUploadResult>(res);
}
